import type {
  AgentBuildProgressEvent,
  AgentStepEvent,
} from '@travel-planner/shared/schemas';
import type { AgentRunStatus, AgentTimelineItem } from '../model/use-agent-run';
import { CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { pickCss } from '@/shared/lib/css-utils';
import { Button, Markdown, Text } from '@/shared/ui';
import { AgentBuildProgress } from './agent-build-progress';
import { AgentConfirmCard } from './agent-confirm-card';
import { AgentEntityRowsCard } from './agent-entity-rows-card';
import css from './agent-run-progress.module.scss';

const rootCss = pickCss(css, css.root);
const stepCss = pickCss(css, css.step);
const finalCss = pickCss(css, css.final);

export type AgentRunProgressProps = {
  // Steps + `entity_rows` card groups in arrival order (P2.7), rendered inline.
  timeline: AgentTimelineItem[];
  // Latest coarse build-progress beat, shown on the live row in place of the
  // flat spinner. Null when this run isn't a multi-step build.
  buildProgress: AgentBuildProgressEvent | null;
  // What the agent is doing right now — the in-flight tool's label, or
  // "Thinking…" between steps. Rendered on the live spinner row.
  activeLabel: string | null;
  finalText: string | null;
  budgetExhausted: boolean;
  // The run stopped on the step budget with work still to do (P4.1) — render
  // the "Keep going" control wired to `onKeepGoing`.
  canContinue?: boolean;
  onKeepGoing?: () => void;
  // Resolve a paused `requiresConfirm` tool's confirm card (P5.1).
  onConfirm?: (callId: string, decision: 'approve' | 'reject') => void;
  // Call ids already decided this run — locks the matching card's buttons.
  resolvedConfirmIds?: ReadonlySet<string>;
  status: AgentRunStatus;
};

// One finished step row — a check (success) or a muted "adjusting" beat (a
// recoverable, mid-chain error the loop retries).
const StepRow = ({ step }: { step: AgentStepEvent }) => (
  <div className={stepCss({ is: step.status })}>
    {step.status === 'success' ? (
      <CheckCircle2 aria-hidden="true" className={css.stepIcon} size={14} />
    ) : (
      <RefreshCw aria-hidden="true" className={css.stepIcon} size={14} />
    )}
    <Text as="span" size="sm" title={step.detail ?? undefined}>
      {step.label}
    </Text>
  </div>
);

// Live progress view for a single `agentRunStream` run. Renders each timeline
// item the moment it streams in — step rows + inline `entity_rows` cards — a
// build-progress bar or spinner while the loop is still going, and the final
// assistant text (or budget-exhausted notice) when it settles.
export const AgentRunProgress = ({
  timeline,
  buildProgress,
  activeLabel,
  finalText,
  budgetExhausted,
  canContinue = false,
  onKeepGoing,
  onConfirm,
  resolvedConfirmIds,
  status,
}: AgentRunProgressProps) => {
  if (status === 'idle') return null;

  return (
    <div className={rootCss()} role="status" aria-live="polite">
      {timeline.map((item, i) => {
        // Timeline items are append-only and never reordered, so index is a
        // stable key.
        if (item.kind === 'step') {
          return <StepRow key={`step-${item.label}-${i}`} step={item} />;
        }
        if (item.kind === 'confirm') {
          return (
            <AgentConfirmCard
              key={`confirm-${item.callId}`}
              event={item}
              disabled={resolvedConfirmIds?.has(item.callId) ?? false}
              onApprove={() => onConfirm?.(item.callId, 'approve')}
              onReject={() => onConfirm?.(item.callId, 'reject')}
            />
          );
        }
        return (
          <AgentEntityRowsCard
            key={`rows-${item.entityType}-${i}`}
            event={item}
          />
        );
      })}

      {status === 'running' &&
        (buildProgress ? (
          // A multi-step build is mid-flight — show "Day 3 of 7" instead of the
          // flat spinner.
          <AgentBuildProgress event={buildProgress} />
        ) : (
          <div className={css.step}>
            <Loader2 aria-hidden="true" className={css.spinner} size={14} />
            <Text as="span" size="sm">
              {activeLabel ?? 'Working…'}
            </Text>
          </div>
        ))}

      {finalText && (
        <div
          className={finalCss({ is: budgetExhausted ? 'budget' : undefined })}
        >
          <Markdown>{finalText}</Markdown>
        </div>
      )}

      {canContinue && onKeepGoing && status === 'done' && (
        // The step budget ran out mid-task (P4.1) — one click resumes the run
        // where it stopped, under a fresh budget.
        <div className={css.keepGoing}>
          <Button onClick={onKeepGoing} size="sm" variant="outline">
            Keep going
          </Button>
        </div>
      )}

      {status === 'error' && !finalText && (
        <Text as="p" size="sm">
          Something went wrong running that request.
        </Text>
      )}
    </div>
  );
};
